import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type HTMLAttributes,
  type ButtonHTMLAttributes,
} from 'react'
import { createPortal } from 'react-dom'
import type { VehicleState } from '../lib/types'

/* ===========================================================================
   Panel — the basic surface. Corner brackets, hairline border, optional
   header rule. Everything in the app sits in one of these.
   =========================================================================== */

interface PanelProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode
  action?: ReactNode
  subtitle?: ReactNode
  bracket?: boolean
  padded?: boolean
  children: ReactNode
}

export function Panel({
  title,
  action,
  subtitle,
  bracket = true,
  padded = true,
  children,
  className = '',
  ...rest
}: PanelProps) {
  return (
    <div className={`panel ${bracket ? 'panel-bracket' : ''} ${className}`} {...rest}>
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 px-3.5 h-10 rule-b">
          <div className="min-w-0">
            <h2 className="label label-bright truncate">{title}</h2>
            {subtitle && <p className="text-[11px] text-[var(--color-ink-3)] truncate">{subtitle}</p>}
          </div>
          {action && <div className="flex items-center gap-1.5 shrink-0">{action}</div>}
        </header>
      )}
      <div className={padded ? 'p-3.5' : ''}>{children}</div>
    </div>
  )
}

/* ===========================================================================
   Metric — a single readout. Large tabular figure, condensed label, optional
   delta. The dashboard is a wall of these.
   =========================================================================== */

export function Metric({
  label,
  value,
  unit,
  tone = 'default',
  hint,
  icon,
}: {
  label: string
  value: ReactNode
  unit?: string
  tone?: 'default' | 'moving' | 'idle' | 'stopped' | 'offline' | 'accent' | 'critical'
  hint?: ReactNode
  icon?: ReactNode
}) {
  const colors: Record<string, string> = {
    default: 'var(--color-ink)',
    moving: 'var(--text-moving)',
    idle: 'var(--text-idle)',
    stopped: 'var(--text-stopped)',
    offline: 'var(--color-ink-3)',
    accent: 'var(--accent-text)',
    critical: 'var(--text-critical)',
  }

  return (
    <div className="panel panel-bracket px-3.5 py-3 flex flex-col gap-1.5 min-w-0">
      <div className="flex items-center gap-1.5">
        {icon && <span className="text-[var(--color-ink-4)]">{icon}</span>}
        <span className="label truncate">{label}</span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span
          className="tnum text-[1.75rem] leading-none font-semibold tracking-tight"
          style={{ color: colors[tone] }}
        >
          {value}
        </span>
        {unit && <span className="text-[11px] text-[var(--color-ink-3)]">{unit}</span>}
      </div>
      {hint && <div className="text-[11px] text-[var(--color-ink-3)] truncate">{hint}</div>}
    </div>
  )
}

/* ===========================================================================
   Status
   =========================================================================== */

// `fill` is the vivid status colour for dots and tints — right on either
// theme. `text` is the readable-on-this-background version for labels.
const STATE_STYLE: Record<VehicleState, { fill: string; text: string; label: string }> = {
  moving: { fill: 'var(--color-moving)', text: 'var(--text-moving)', label: 'Moving' },
  idle: { fill: 'var(--color-idle)', text: 'var(--text-idle)', label: 'Idle' },
  stopped: { fill: 'var(--color-stopped)', text: 'var(--text-stopped)', label: 'Stopped' },
  offline: { fill: 'var(--color-offline)', text: 'var(--color-ink-3)', label: 'No signal' },
}

export function StateDot({ state, live = false }: { state: VehicleState; live?: boolean }) {
  const s = STATE_STYLE[state] ?? STATE_STYLE.offline
  return (
    <span
      className={`dot ${live && state === 'moving' ? 'dot-live' : ''}`}
      style={{ background: s.fill, color: s.fill }}
      aria-label={s.label}
    />
  )
}

export function StateChip({ state, live = false }: { state: VehicleState; live?: boolean }) {
  const s = STATE_STYLE[state] ?? STATE_STYLE.offline
  return (
    <span
      className="chip"
      style={{
        color: s.text,
        borderColor: `color-mix(in srgb, ${s.fill} 40%, transparent)`,
        background: `color-mix(in srgb, ${s.fill} 12%, transparent)`,
      }}
    >
      <StateDot state={state} live={live} />
      {s.label}
    </span>
  )
}

export function Chip({
  children,
  tone = 'neutral',
  title,
}: {
  children: ReactNode
  tone?: 'neutral' | 'accent' | 'ok' | 'warn' | 'critical' | 'muted'
  title?: string
}) {
  const tones: Record<string, { fill: string; text: string }> = {
    neutral: { fill: 'var(--color-ink-2)', text: 'var(--color-ink-2)' },
    accent: { fill: 'var(--accent)', text: 'var(--accent-text)' },
    ok: { fill: 'var(--color-ok)', text: 'var(--text-ok)' },
    warn: { fill: 'var(--color-warn)', text: 'var(--text-warn)' },
    critical: { fill: 'var(--color-critical)', text: 'var(--text-critical)' },
    muted: { fill: 'var(--color-ink-4)', text: 'var(--color-ink-3)' },
  }
  const c = tones[tone]!

  return (
    <span
      className="chip"
      title={title}
      style={{
        color: c.text,
        borderColor: `color-mix(in srgb, ${c.fill} 32%, transparent)`,
        background: `color-mix(in srgb, ${c.fill} 9%, transparent)`,
      }}
    >
      {children}
    </span>
  )
}

export function SeverityChip({ severity }: { severity: string }) {
  const tone = severity === 'critical' ? 'critical' : severity === 'warning' ? 'warn' : 'muted'
  return <Chip tone={tone}>{severity}</Chip>
}

/* ===========================================================================
   Feedback
   =========================================================================== */

export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className="animate-spin"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.18" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2.5 py-14 text-[var(--color-ink-3)]">
      <Spinner />
      <span className="label">{label}</span>
    </div>
  )
}

export function EmptyState({
  title,
  body,
  action,
  icon,
}: {
  title: string
  body?: string
  action?: ReactNode
  icon?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2.5 py-14 px-6 text-center">
      {icon && <div className="text-[var(--color-ink-4)] mb-1">{icon}</div>}
      <h3 className="label label-bright">{title}</h3>
      {body && <p className="text-[13px] text-[var(--color-ink-3)] max-w-sm leading-relaxed">{body}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Something went wrong'
  return (
    <div className="flex flex-col items-center justify-center gap-2.5 py-12 px-6 text-center">
      <div
        className="w-8 h-8 grid place-items-center rounded-[3px] border"
        style={{
          color: 'var(--text-critical)',
          borderColor: 'color-mix(in srgb, var(--color-critical) 35%, transparent)',
          background: 'color-mix(in srgb, var(--color-critical) 10%, transparent)',
        }}
      >
        !
      </div>
      <h3 className="label" style={{ color: 'var(--text-critical)' }}>
        Request failed
      </h3>
      <p className="text-[13px] text-[var(--color-ink-2)] max-w-sm">{message}</p>
      {retry && (
        <button className="btn btn-sm mt-1" onClick={retry}>
          Try again
        </button>
      )}
    </div>
  )
}

/* ===========================================================================
   Modal
   =========================================================================== */

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = 520,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
  width?: number
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    // Move focus into the dialog so the keyboard does not stay behind it.
    ref.current?.querySelector<HTMLElement>('input,select,textarea,button')?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: 'var(--backdrop)', backdropFilter: 'blur(3px)' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        ref={ref}
        className="panel panel-bracket w-full max-h-[86vh] flex flex-col"
        style={{ maxWidth: width, boxShadow: 'var(--shadow-modal)' }}
      >
        <header className="flex items-center justify-between px-4 h-11 rule-b shrink-0">
          <h2 className="label label-bright">{title}</h2>
          <button className="btn btn-ghost btn-sm px-1.5" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="p-4 overflow-y-auto grow">{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 px-4 h-13 py-2.5 rule-t shrink-0">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  )
}

/* ===========================================================================
   Forms
   =========================================================================== */

export function Field({
  label,
  children,
  hint,
  error,
  required,
}: {
  label: string
  children: ReactNode
  hint?: string
  error?: string
  required?: boolean
}) {
  return (
    <label className="block">
      <span className="label block mb-1.5">
        {label}
        {required && <span style={{ color: 'var(--text-critical)' }}> *</span>}
      </span>
      {children}
      {error ? (
        <span className="block mt-1 text-[11px]" style={{ color: 'var(--text-critical)' }}>
          {error}
        </span>
      ) : hint ? (
        <span className="block mt-1 text-[11px] text-[var(--color-ink-4)]">{hint}</span>
      ) : null}
    </label>
  )
}

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex items-center gap-2.5 group"
    >
      <span
        className="relative w-8 h-[18px] rounded-full border transition-colors duration-150"
        style={{
          background: checked ? 'var(--accent)' : 'var(--color-hull)',
          borderColor: checked ? 'var(--accent)' : 'var(--color-rule-bright)',
        }}
      >
        <span
          className="absolute top-[2px] w-3 h-3 rounded-full transition-all duration-150"
          style={{
            left: checked ? 'calc(100% - 14px)' : '2px',
            background: checked ? 'var(--accent-ink)' : 'var(--color-ink-3)',
          }}
        />
      </span>
      <span className="text-[13px] text-[var(--color-ink-2)] group-hover:text-[var(--color-ink)]">
        {label}
      </span>
    </button>
  )
}

/* ===========================================================================
   Toasts
   =========================================================================== */

type Toast = { id: number; tone: 'ok' | 'error' | 'info'; message: string }

let pushToast: ((t: Omit<Toast, 'id'>) => void) | null = null

export function toast(message: string, tone: Toast['tone'] = 'info') {
  pushToast?.({ message, tone })
}

export function ToastHost() {
  const [items, setItems] = useState<Toast[]>([])

  useEffect(() => {
    pushToast = (t) => {
      const id = Date.now() + Math.random()
      setItems((prev) => [...prev, { ...t, id }])
      window.setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== id)), 5000)
    }
    return () => {
      pushToast = null
    }
  }, [])

  if (!items.length) return null

  const tones: Record<Toast['tone'], string> = {
    ok: 'var(--color-ok)',
    error: 'var(--color-critical)',
    info: 'var(--accent)',
  }

  return createPortal(
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 pointer-events-none">
      {items.map((t) => (
        <div
          key={t.id}
          className="panel panel-glass px-3.5 py-2.5 flex items-center gap-2.5 min-w-[260px] max-w-sm pointer-events-auto"
          style={{
            borderLeft: `2px solid ${tones[t.tone]}`,
            animation: 'rise 0.3s var(--ease-glide) both',
            boxShadow: 'var(--shadow-toast)',
          }}
          role="status"
        >
          <span className="dot" style={{ background: tones[t.tone] }} />
          <span className="text-[13px] text-[var(--color-ink)] leading-snug">{t.message}</span>
        </div>
      ))}
    </div>,
    document.body,
  )
}

/* ===========================================================================
   Misc
   =========================================================================== */

export function Button({
  variant = 'default',
  size,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'primary' | 'ghost' | 'danger'
  size?: 'sm'
}) {
  const v = variant === 'default' ? '' : `btn-${variant}`
  return <button className={`btn ${v} ${size ? `btn-${size}` : ''} ${className}`} {...rest} />
}

/** A compact inline bar, used for utilisation and idle share. */
export function Bar({ value, max = 100, tone = 'accent' }: { value: number; max?: number; tone?: string }) {
  const colors: Record<string, string> = {
    accent: 'var(--accent)',
    ok: 'var(--color-ok)',
    warn: 'var(--color-warn)',
    critical: 'var(--color-critical)',
  }
  const pct = Math.max(0, Math.min(100, (value / max) * 100))

  return (
    <div className="h-1.5 w-full rounded-[2px] overflow-hidden" style={{ background: 'var(--color-hull)' }}>
      <div
        className="h-full transition-[width] duration-500"
        style={{ width: `${pct}%`, background: colors[tone] ?? colors.accent }}
      />
    </div>
  )
}

/** Inline sparkline for trends; an SVG polyline is plenty and avoids pulling
 *  a charting library into a page that shows one 40px graph. */
export function Sparkline({
  values,
  width = 96,
  height = 24,
  color = 'var(--accent)',
}: {
  values: number[]
  width?: number
  height?: number
  color?: string
}) {
  if (values.length < 2) return <div style={{ width, height }} />

  const max = Math.max(...values, 1)
  const min = Math.min(...values, 0)
  const span = max - min || 1
  const step = width / (values.length - 1)

  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(height - ((v - min) / span) * height).toFixed(1)}`)
    .join(' ')

  return (
    <svg width={width} height={height} className="overflow-visible" aria-hidden>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  )
}
